import { Component, inject, input, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { UiComponentsModule } from '@mwe/ui/components';
import { CommonModule } from '@angular/common';
import { MeterPointSelection, SELECTED_METER_POINT_PRODUCTS } from '../../../../../model/product-selection.model';
import { MeterPoint } from '../../../../../model/meter-point.model';
import { CustomValidators } from '@shared-ikp/utils/validators';
import { MeterPointChangeSignalState } from '../../../meterpoint-change-request.types';
import { SignalState } from '@shared-ikp/service/signal-state-service/signal-state.service';
import { getZaehlpunktnummerShort } from '../../../meter-point-change-request.utils';
import { InfoInputReadOnlyComponent } from '@shared-ikp/components/inputs/info-input-read-only/info-input-read-only.component';

class MeterpointCheckboxSelection {
  isMeterPointSelected: boolean;
  currentMeterPoint: MeterPointSelection | null;
}

@Component({
  selector: 'ikp-product-selection',
  templateUrl: './product-selection.component.html',
  standalone: true,
  styleUrl: './product-selection.component.scss',
  imports: [UiComponentsModule, CommonModule, ReactiveFormsModule, InfoInputReadOnlyComponent],
})
export class ProductSelectionComponent implements OnInit {
  protected wizardStore: MeterPointChangeSignalState = inject(SignalState);

  meterPoint = input<MeterPoint>();
  hasProductsSelectedError = input<boolean>();
  isMeterNumberEditable = signal<boolean>(false);

  categoryIcon: string;
  buttonIsFocused = false;
  formGroup: FormGroup;

  formBuilder = inject(FormBuilder);
  parentForm = input<FormGroup>();

  ngOnInit() {
    const currentFormState = this.wizardStore.state().productAndDate?.meterPoints;
    const currentMeterPoint = currentFormState?.find(meterPoint => meterPoint.id === this.meterPoint().id);
    this.parentForm().addControl(SELECTED_METER_POINT_PRODUCTS, this.formBuilder.group({}));
    this.initFormWithSelectedMeterpoint(this.meterPoint().id, currentMeterPoint);
    this.categoryIcon = this.meterPoint().sparte.toLowerCase();
  }

  private initFormWithSelectedMeterpoint(meterPointId: string, currentMeterPoint?: MeterPointSelection) {
    const productsForm = this.parentForm().get(SELECTED_METER_POINT_PRODUCTS) as FormGroup;
    const meterPointSelection = {
      isMeterPointSelected: true,
      currentMeterPoint: currentMeterPoint,
    };
    this.addSelectedMeterpointForm(meterPointSelection);
    productsForm.addControl(meterPointId, this.formGroup);
  }

  isInputInvalid(key: string) {
    const form = this.parentForm()?.get(key);
    return form && form.touched && form.invalid;
  }

  isMeterPointSelected() {
    const productsForm = this.parentForm().get(SELECTED_METER_POINT_PRODUCTS) as FormGroup;
    return productsForm.get(this.meterPoint().id)?.get(this.IS_METER_POINT_SELECTED_KEY)?.value;
  }

  getFormError(key: string): string {
    const formKey = this.parentForm().get(key);
    if (formKey?.errors) {
      return formKey.errors.translationKey;
    }
    return null;
  }

  onEditMeterNumber() {
    this.isMeterNumberEditable.set(true);
  }

  private addSelectedMeterpointForm(meterPointSelection: MeterpointCheckboxSelection) {
    const meterNumber = meterPointSelection.currentMeterPoint?.meterNumber ?? this.meterPoint().zaehlerNr ?? '';

    this.formGroup = this.formBuilder.group({
      isMeterPointSelected: meterPointSelection.isMeterPointSelected,
      meterPointNumber: [this.meterPoint().zaehlpunktnummer, [CustomValidators.meterPointNumberValidator]],
      meterNumber: [meterNumber, [CustomValidators.meterNumberValidator]],
      meterCount: [meterPointSelection.currentMeterPoint?.meterCount ?? '', [Validators.min(0), CustomValidators.meterCountValidator]],
    });
  }

  protected readonly SELECTED_METER_POINTS_KEY = SELECTED_METER_POINT_PRODUCTS;
  protected readonly IS_METER_POINT_SELECTED_KEY = 'isMeterPointSelected';
  protected readonly getZaehlpunktnummerShort = getZaehlpunktnummerShort;
}
